


















import Vue from 'vue'
import { Prop, Component } from 'nuxt-property-decorator'
import { PublicProject } from '~/models/project'
import LotInfo from '~/models/lotInfo'
import HubspotForm from '~/models/hubspotForm'
import { createMarketingLeadUser } from '~/api/marketing.api'
import { MarketingLeadData } from '~/models/marketing'
import { ConsumerMapRoutes } from '~/enums/consumerMap'

@Component
export default class HubspotFormComponent extends Vue {
  @Prop({ default: null }) project: PublicProject
  @Prop({ default: () => {} }) lot: LotInfo
  @Prop({ default: (): Array<LotInfo> => [] }) lots: Array<LotInfo>
  @Prop({ default: () => {} }) lotInfoSelected: LotInfo
  @Prop({ default: null }) hubspotConfig: HubspotForm
  @Prop({ default: null }) planSelected: any
  @Prop({ default: null }) elevationSelected: any

  containerHeight: string = 'calc(100vh - 96px)'

  mounted() {
    this.injectHubSpotScript()
    this.calculateContainerHeight()
    window.addEventListener('resize', this.calculateContainerHeight)
  }

  beforeDestroy() {
    this.cleanupHubSpotForm()
    window.removeEventListener('resize', this.calculateContainerHeight)
  }

  getActiveRoute(): string {
    return this.$route.path.split('/').pop()
  }

  isFavoritesRoute(): boolean {
    return this.getActiveRoute() === ConsumerMapRoutes.FAVORITES
  }

  injectHubSpotScript() {
    if (
      !document.querySelector(
        'script[src="https://js.hsforms.net/forms/v2.js"]'
      )
    ) {
      const script = document.createElement('script')
      script.src = 'https://js.hsforms.net/forms/v2.js'
      script.async = true
      document.head.appendChild(script)
      script.onload = () => {
        this.injectForm()
      }
    } else {
      this.injectForm()
    }
  }

  injectForm() {
    const hbspt = (window as any).hbspt
    const formContainer = document.querySelector('.hsFormWrap')
    if (formContainer) {
      const formConfig: any = {
        portalId: this.hubspotConfig.portal_id,
        formId: this.hubspotConfig.form_id,
        pageName: this.hubspotConfig.page_name,
        target: '.hsFormWrap',
        contentType: this.hubspotConfig.content_type,
        onFormReady: () => {
          if (this.isFavoritesRoute()) {
            this.injectFavoritesIntoHiddenFields()
          } else {
            this.injectLotIntoHiddenFields()
          }
          this.addFormSubmitListener()
        },
      }

      if (this.hubspotConfig.css !== '') {
        formConfig.css = this.hubspotConfig.css
      }

      hbspt.forms.create(formConfig)
    }
  }

  injectFavoritesIntoHiddenFields() {
    const formattedLots = this.formatLotsData(this.lots)
    const jsonString = JSON.stringify(formattedLots)
    const iframe = document.querySelector(
      '.hs-form-iframe'
    ) as HTMLIFrameElement

    if (iframe) {
      const input = iframe.contentDocument?.querySelector(
        `input[name=favorites]`
      ) as HTMLInputElement
      if (input) {
        input.value = jsonString
      }
    }
  }

  formatLotsData(lots: Array<any>): Array<any> {
    return lots.map((lot) => ({
      builder: lot.builder?.name,
      parcel_id: lot.parcel_id,
      lot_size: lot.size,
      lot_phase: lot.phase,
      lot_number: lot.number,
      street_number: lot.street_number,
      street_name: lot.street_name,
      sqft: lot.architectural_submission?.master_plan?.plan?.sqft || lot.sqft,
      listing_price:
        lot.architectural_submission?.master_plan?.plan?.price ||
        lot.listing_price,
      segmentation: lot.segmentation,
      plan: lot.architectural_submission?.master_plan?.plan?.name,
      plan_id: lot.architectural_submission?.master_plan?.plan?.id,
      elevation: lot.architectural_submission?.master_plan?.name,
      elevation_id: lot.architectural_submission?.master_plan?.id,
    }))
  }

  injectLotIntoHiddenFields() {
    const hiddenFields: any = {}
    const lotInfo = this.lot
    const planSelected = this.planSelected
    this.addValidFields(hiddenFields, 'builder', lotInfo.builder?.name)
    this.addValidFields(hiddenFields, 'parcel_id', lotInfo.parcel_id)
    this.addValidFields(hiddenFields, 'lot_size', lotInfo.size)
    this.addValidFields(hiddenFields, 'lot_phase', lotInfo.phase)
    this.addValidFields(hiddenFields, 'lot_number', lotInfo.number)
    this.addValidFields(hiddenFields, 'street_number', lotInfo.street_number)
    this.addValidFields(hiddenFields, 'street_name', lotInfo.street_name)
    this.addValidFields(hiddenFields, 'home_sqft', lotInfo.sqft)
    this.addValidFields(hiddenFields, 'listing_price', lotInfo.listing_price)
    this.addValidFields(hiddenFields, 'segmentation', lotInfo.segmentation)

    if (planSelected) {
      this.addValidFields(
        hiddenFields,
        'plan',
        `${planSelected.name} (${planSelected.id})`
      )
      if (lotInfo.type === 'home site') {
        this.addValidFields(hiddenFields, 'home_sqft', planSelected.sqft)
        this.addValidFields(hiddenFields, 'listing_price', planSelected.price)
      }
    }

    if (this.elevationSelected) {
      this.addValidFields(
        hiddenFields,
        'elevation',
        `${this.elevationSelected.name} (${this.elevationSelected.id})`
      )
    }

    const iframe = document.querySelector(
      '.hs-form-iframe'
    ) as HTMLIFrameElement

    if (iframe) {
      Object.keys(hiddenFields).forEach((key) => {
        const input = iframe.contentDocument?.querySelector(
          `input[name=${key}]`
        ) as HTMLInputElement
        if (input) {
          input.value = hiddenFields[key]
        }
      })
    }
  }

  addValidFields(obj: any, key: string, value: any) {
    if (value !== undefined && value !== null && value !== '-') {
      obj[key] = value
    }
  }

  getInputValue(form: HTMLFormElement, name: string) {
    const input = form.querySelector(`input[name=${name}]`) as HTMLInputElement
    return input?.value
  }

  addFormSubmitListener() {
    const iframe = document.querySelector(
      '.hs-form-iframe'
    ) as HTMLIFrameElement

    if (iframe) {
      const form = iframe.contentDocument?.querySelector('form')
      if (form) {
        form.addEventListener('submit', this.handleFormSubmit)
      }
    }
  }

  async handleFormSubmit(event: Event) {
    const form = event.target as HTMLFormElement
    if (form.checkValidity()) {
      const email = this.getInputValue(form, 'email')
      const firstName = this.getInputValue(form, 'firstname')
      const lastName = this.getInputValue(form, 'lastname')
      const phone =
        this.getInputValue(form, 'mobilephone') ||
        this.getInputValue(form, 'phone')

      const marketingLeadData: MarketingLeadData = {
        email,
        first_name: firstName,
        last_name: lastName,
        phone,
        project_id: this.project.id,
        parcel_id: this.lot.parcel_id,
        builder_id: this.lot.builder.id,
      }

      try {
        await createMarketingLeadUser(marketingLeadData)
      } catch (error) {
        console.error('Error creating user:', error)
      }
    }
  }

  cleanupHubSpotForm() {
    const formContainer = document.querySelector('.hsFormWrap')
    if (formContainer) {
      formContainer.remove()
    }
    const newFormContainer = document.createElement('div')
    newFormContainer.className = 'hsFormWrap'
    document.body.appendChild(newFormContainer)
  }

  calculateContainerHeight() {
    const screenWidth = window.innerWidth
    let navbarHeight = 56

    if (screenWidth > 600 && screenWidth < 1024) {
      navbarHeight = 40
    }

    const propertyListNavbarHeight = 40
    this.containerHeight = `calc(100vh - ${
      navbarHeight + propertyListNavbarHeight
    }px)`
  }
}
